import IosShareIcon from "@mui/icons-material/IosShare";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { getOrInitTwilioClient } from "app/DesktopLayout/twilioClient";
import BoatImage from "assets/images/boat.png";
import { baseWebUrl } from "lib/constants";
import { useDeleteAvailabilityMutation } from "lib/features/availability/availabilityService";
import { Availability } from "lib/features/availability/types";
import { Item } from "lib/features/item/types";
import {
  selectCurrentChannelUrl,
  selectIsExistingBookingLink,
  setIsExistingBookingLink,
} from "lib/features/message/messageSlice";
import { CreateReferralDto } from "lib/features/referral/referralService";
import { selectUser } from "lib/features/user/userSlice";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import chatServices from "services/chat-service";
import referralServices from "services/referral-service";
import { availabilityScreenStyles } from "styles/AvailabilityDirectory/availabilityScreen.styles";
import { formatDurationTime } from "util/commonHelper";
import { parseImageUrl } from "util/parseImageUrl";
import BinIcon from "../../../assets/icons/delete_referral.svg";
import DeleteAvailabilityModal from "./DeleteAvailabilityModal";
import EditAvailabilityModal from "./EditAvailabilityModal";
import ShareAvailabilityModal from "./ShareAvailabilityModal";

interface AvailabilityProps {
  item: Item;
  availability: Availability;
  onClose: () => void;
}

const AvailabilityCard = ({
  item,
  availability,
  onClose,
}: AvailabilityProps) => {
  console.log("selected item: ", item);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);
  const currentChannelUrl = useSelector(selectCurrentChannelUrl);

  const fallbackImage = parseImageUrl(item?.itemImages?.[0]?.uri) || BoatImage;
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const [isShareModal, setIsShareModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [deleteAvailabilityMutation, { isLoading: deleteLoading }] =
    useDeleteAvailabilityMutation();

  const handleDeleteAvailability = async () => {
    try {
      await deleteAvailabilityMutation(availability.id).unwrap();
      setIsDeleteModal(false);
      onClose();
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const handleShare = async () => {
    if (!user?.id || !availability || !currentChannelUrl) {
      console.error(
        "Missing required data: user, availability, or channel URL",
      );
      return;
    }

    try {
      setLoading(true);

      const referralPayload: CreateReferralDto = {
        user_id: user.id,
        business_id: item.business.id,
        availability_id: availability.id,
        item_id: item.id,
        referral_type: 1,
        state: 1,
      };

      const referralResponse =
        await referralServices.createReferral(referralPayload);
      const referralId = referralResponse?.data?.id;

      if (!referralId) {
        throw new Error("Missing referral id from createReferral response");
      }

      const url = `${baseWebUrl}/checkout/${referralId}`;

      // Initialize Twilio client if needed and get the conversation
      const fetchToken = async () => {
        const res = await chatServices.getUserTokenForChat(String(user.id));
        return String(res.data.token);
      };

      const client = await getOrInitTwilioClient(fetchToken);
      // currentChannelUrl may contain full path: {sid}/{avatar}/{name}/{myavatar}
      // Extract just the SID for the API call
      const conversationSid = currentChannelUrl.split("/")[0];
      const conversation = await client.getConversationBySid(conversationSid);

      // Send the message directly via Twilio SDK
      await conversation.sendMessage(url);
      dispatch(setIsExistingBookingLink(false));
      // Navigate back to the chat
      navigate(`/chat/${currentChannelUrl}`);
    } catch (error) {
      console.error("Error sending message to conversation:", error);
    } finally {
      setLoading(false);
    }
  };

  const refereeName = (() => {
    const name = decodeURIComponent(currentChannelUrl)?.split("/")?.[2];
    const parts = name?.split(" ") ?? [];
    return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
  })();

  return (
    <Box sx={availabilityScreenStyles.card(theme)}>
      {/* Image Box */}
      <Box sx={availabilityScreenStyles.imageBox}>
        <img
          src={fallbackImage}
          alt={"image"}
          style={availabilityScreenStyles.image}
        />
      </Box>

      <Box maxHeight="262px" p={2}>
        {/* Body */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={availabilityScreenStyles.titleText}>
              {item?.itemDetails[0]?.item_name}
            </Typography>
            <Box sx={availabilityScreenStyles.availabilityBadge}>
              <Typography style={availabilityScreenStyles.availabilityText}>
                {availability?.quantity} Available
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={[availabilityScreenStyles.subtitleText, { paddingTop: "2px" }]}
          >
            {item?.itemDetails[0]?.headline}
          </Typography>
        </Box>

        {/* Details Section */}
        <Box sx={availabilityScreenStyles.detailContainer}>
          {/* First Row */}
          <Box sx={availabilityScreenStyles.rowContainer}>
            {/* Time and Duration section */}
            <Box sx={availabilityScreenStyles.timeContainer}>
              <Typography sx={availabilityScreenStyles.rowText}>
                {moment
                  .utc(availability?.start_time)
                  .tz("America/New_York")
                  .format("hh:mm A")}
              </Typography>

              {item &&
                item.itemDetails[0].duration_applicable &&
                item.itemDetails[0].duration && (
                  <>
                    <Typography
                      sx={[
                        availabilityScreenStyles.rowText,
                        availabilityScreenStyles.dotText,
                      ]}
                    >
                      •
                    </Typography>

                    <Typography sx={availabilityScreenStyles.rowText}>
                      {formatDurationTime(item.itemDetails[0].duration) + " hr"}
                    </Typography>
                  </>
                )}
            </Box>
            <Box sx={availabilityScreenStyles.priceSection}>
              <Typography sx={availabilityScreenStyles.priceLabel}>
                Price:
              </Typography>
              <Typography sx={availabilityScreenStyles.priceValue}>
                ${item?.itemDetails[0]?.price}
              </Typography>
            </Box>

            <Box sx={availabilityScreenStyles.priceSection}>
              <Typography sx={availabilityScreenStyles.priceLabel}>
                Pays:
              </Typography>
              <Typography sx={availabilityScreenStyles.priceValue}>
                {`${item?.itemDetails[0]?.comission}%`} (
                {`$${
                  (item?.itemDetails[0]?.price *
                    (item?.itemDetails[0]?.comission || 0)) /
                  100
                }`}
                )
              </Typography>
            </Box>

            {availability.private && (
              <Box sx={availabilityScreenStyles.priceSection}>
                <Typography sx={availabilityScreenStyles.priceLabel}>
                  Referee :
                </Typography>
                <Typography sx={availabilityScreenStyles.priceValue}>
                  {availability?.referrals?.[0]?.business?.business
                    ? availability?.referrals?.[0]?.business?.business
                    : availability?.referrals?.[0]?.user?.first_name &&
                        availability?.referrals?.[0]?.user?.last_name
                      ? `${availability?.referrals?.[0]?.user?.first_name} ${availability?.referrals?.[0]?.user?.last_name}`
                      : availability.referrals?.[0]?.customer_name || "Unknown"}
                </Typography>
              </Box>
            )}
            {currentChannelUrl &&
              !availability.private &&
              isExistingBookingLink && (
                <Box sx={availabilityScreenStyles.priceSection}>
                  <Typography sx={availabilityScreenStyles.priceLabel}>
                    Referee :
                  </Typography>
                  <Typography sx={availabilityScreenStyles.priceValue}>
                    {refereeName}
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>

        {/* Actions */}
        <Box
          height="84px"
          display="flex"
          justifyContent="center"
          gap={2}
          alignItems="center"
        >
          {!availability.private && (
            <>
              {loading ? (
                <Button
                  variant="contained"
                  disabled
                  sx={{
                    width: "174px",
                    height: "44px",
                    "&.Mui-disabled": { backgroundColor: "#235e24" },
                  }}
                >
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={{ width: "174px", height: "44px" }}
                  startIcon={<IosShareIcon />}
                  onClick={() => {
                    if (isExistingBookingLink) {
                      handleShare();
                      setLoading(true);
                    } else {
                      setIsShareModal(true);
                    }
                  }}
                >
                  Share
                </Button>
              )}
            </>
          )}
          {!isExistingBookingLink && (
            <Button
              variant="outlined"
              color="inherit"
              sx={{ width: "174px", height: "44px" }}
              startIcon={<ModeEditIcon />}
              onClick={() => {
                setIsEditModal(true);
              }}
            >
              Edit
            </Button>
          )}
          {!isExistingBookingLink && (
            <Button
              variant="outlined"
              color="inherit"
              sx={{ width: "174px", height: "44px" }}
              startIcon={<img src={BinIcon} width={20} height={20} />}
              onClick={() => {
                setIsDeleteModal(true);
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Delete Modal */}
      <DeleteAvailabilityModal
        open={isDeleteModal}
        close={() => setIsDeleteModal(false)}
        loading={deleteLoading}
        onSubmit={handleDeleteAvailability}
      />

      {/* Edit Modal */}
      <EditAvailabilityModal
        open={isEditModal}
        close={() => setIsEditModal(false)}
        selectedId={item.id}
        selectedAvailability={availability}
        item={item}
        setItem={() => {}} // No longer needed since we're using props
      />

      <ShareAvailabilityModal
        open={isShareModal}
        close={() => setIsShareModal(false)}
        selectedAvailability={availability}
        item={item}
      />
    </Box>
  );
};

export default AvailabilityCard;
